import { motion } from 'framer-motion';
import { Calendar, Clock, FileText, MessageSquare, Bell, Users, Globe, Shield, ImageUp } from 'lucide-react';

export const Features = () => {
  const features = [
    {
      icon: Calendar,
      title: 'Schedule Meetings',
      description: 'Book appointments, set up calls, and manage your calendar with a simple message.',
      span: 'lg:col-span-4',
      accent: 'from-blue-500/20 to-cyan-500/20',
    },
    {
      icon: Bell,
      title: 'Smart Reminders',
      description: 'Never miss important tasks. AssistlyAI reminds you at the right time.',
      span: 'lg:col-span-4',
      accent: 'from-amber-500/20 to-orange-500/20',
    },
    {
      icon: FileText,
      title: 'Summarise Notes',
      description: 'Send long documents or meeting notes. Get crisp summaries instantly.',
      span: 'lg:col-span-4',
      accent: 'from-purple-500/20 to-pink-500/20',
    },
    {
      icon: ImageUp,
      title: 'Upload documents & images',
      description:
        'Share tickets, PDFs, or photos (like a boarding pass). Ask in your own words—“What’s my PNR?”—and AssistlyAI fetches the details from what you uploaded.',
      span: 'lg:col-span-12',
      accent: 'from-sky-500/20 to-indigo-500/20',
    },
    {
      icon: Clock,
      title: 'Time Management',
      description: 'Track how you spend your day. Get insights to work smarter, not harder.',
      span: 'lg:col-span-6',
      accent: 'from-emerald-500/20 to-teal-500/20',
    },
    {
      icon: Globe,
      title: 'Multilingual Support',
      description: 'Speak in English, Hindi, or Hinglish. AssistlyAI understands all three perfectly.',
      span: 'lg:col-span-6',
      accent: 'from-rose-500/20 to-red-500/20',
    },
    {
      icon: MessageSquare,
      title: 'Natural Conversations',
      description: 'No commands to remember. Just chat like you would with a real assistant.',
      span: 'lg:col-span-4',
      accent: 'from-[#25D366]/20 to-emerald-500/20',
    },
    {
      icon: Users,
      title: 'Team Coordination',
      description: 'Sync with your team. Share schedules and coordinate meetings effortlessly.',
      span: 'lg:col-span-4',
      accent: 'from-indigo-500/20 to-blue-500/20',
    },
    {
      icon: Shield,
      title: 'Private & Secure',
      description: 'Your data stays encrypted. We never share your information with anyone.',
      span: 'lg:col-span-4',
      accent: 'from-slate-500/20 to-gray-500/20',
    },
  ];

  const container = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { staggerChildren: 0.1 },
    },
  };

  const item = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.5 } },
  };

  return (
    <section id="features" className="py-24 lg:py-32 bg-slate-50/50 dark:bg-slate-900/50" data-testid="features-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <span className="text-[#25D366] font-semibold text-sm uppercase tracking-wider">Features</span>
          <h2 className="mt-3 text-3xl sm:text-4xl lg:text-5xl font-bold text-slate-900 dark:text-white">
            One WhatsApp number.
            <br />
            <span className="text-slate-500 dark:text-slate-400">Infinite productivity.</span>
          </h2>
        </motion.div>

        {/* Bento Grid */}
        <motion.div
          variants={container}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-12 gap-4 lg:gap-6"
        >
          {features.map((feature, index) => {
            const Icon = feature.icon;
            return (
              <motion.div
                key={index}
                variants={item}
                className={`${feature.span} group`}
              >
                <div className="h-full bg-white dark:bg-slate-800/50 border border-slate-200 dark:border-slate-700/50 rounded-2xl p-6 lg:p-8 hover:shadow-xl dark:hover:shadow-slate-900/50 transition-all duration-300 hover:-translate-y-1">
                  <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${feature.accent} flex items-center justify-center mb-4 group-hover:scale-110 transition-transform duration-300`}>
                    <Icon className="w-6 h-6 text-slate-700 dark:text-slate-200" />
                  </div>
                  <h3 className="text-xl font-bold text-slate-900 dark:text-white mb-2">
                    {feature.title}
                  </h3>
                  <p className="text-slate-600 dark:text-slate-400 leading-relaxed">
                    {feature.description}
                  </p>
                </div>
              </motion.div>
            );
          })}
        </motion.div>
      </div>
    </section>
  );
};
